// src/pages/league/LeagueListPage.tsx — "Record Book" identity rebuild
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PlusIcon, Cog6ToothIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useSeasonNow, datelineFor } from '../../hooks/useSeasonNow';
import { standingSummary } from '../../utils/leagueStanding';
import Goalpost from '../../components/common/Goalpost';
import StatusChip from '../../components/league/StatusChip';
import apiService from '../../services/api';
import { League } from '../../types';
import { useAuth } from '../../context/AuthContext';


export const LeagueListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: leagues, loading, error, refetch } = useApi(() => apiService.getLeagues());
  const dateline = datelineFor(useSeasonNow());


  // One create route, so every creator lands on the invite screen. The modal
  // that used to live here duplicated the whole form and ended with a bare
  // navigate(), which is where most leagues stopped at one member.
  const createButton = (
    <Link
      to="/app/leagues/create"
      className="inline-flex items-center gap-1.5 bg-gold px-3.5 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-gold-600 transition-colors"
    >
      <PlusIcon className="w-4 h-4" />
      Create League
    </Link>
  );

  return (
    <div className="space-y-8">
      {/* ── Masthead ─────────────────────────────────────────────── */}
      <header className="border-b-2 border-ink-900 pb-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary-500">
              {dateline}
            </p>
            <h1 className="mt-2 font-display uppercase leading-none text-ink-900 text-4xl sm:text-5xl">
              My Leagues
            </h1>
            <p className="mt-2 text-secondary-600">Every league you run or play in.</p>
          </div>
          {createButton}
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────── */}
      {loading ? (
        <ul className="divide-y divide-secondary-200">
          {[0, 1, 2, 3, 4].map(i => (
            <li key={i} className="flex items-center justify-between py-4 animate-pulse">
              <div className="space-y-2">
                <div className="h-4 w-48 bg-secondary-200" />
                <div className="h-3 w-64 bg-secondary-100" />
              </div>
              <div className="h-4 w-4 bg-secondary-200" />
            </li>
          ))}
        </ul>
      ) : error ? (
        <div className="py-12 text-center">
          <p className="font-display uppercase text-lg text-ink-900">Couldn’t load your leagues</p>
          <p className="mt-1 text-sm text-secondary-600">
            Something interrupted the connection. Give it another try.
          </p>
          <button
            onClick={() => refetch().catch(() => {})}
            className="mt-4 inline-flex items-center gap-1.5 border-2 border-ink-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors"
          >
            Retry
          </button>
        </div>
      ) : leagues && leagues.length > 0 ? (
        <ul className="divide-y divide-secondary-200">
          {leagues.map(league => (
            <LeagueRow
              key={league.id}
              league={league}
              isCommissioner={league.commissioner_id === user?.id}
              onOpen={() => navigate(`/app/leagues/${league.id}`)}
            />
          ))}
        </ul>
      ) : (
        <div className="py-16 text-center">
          <Goalpost className="mx-auto w-16 h-14 text-gold mb-4" />
          <p className="font-display uppercase text-lg text-ink-900">No leagues on the books yet</p>
          <p className="mt-1 mb-6 text-sm text-secondary-600 max-w-md mx-auto">
            Start one of your own, or join a league that is already taking members.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/app/leagues/create"
              className="inline-flex items-center gap-1.5 bg-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-gold-600 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Create Your First League
            </Link>
            {/* A third of signups never joined anything: creating was the only
                door out of this screen. */}
            <Link
              to="/app/leagues/browse"
              className="inline-flex items-center gap-1.5 border border-ink-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors"
            >
              Browse Open Leagues
            </Link>
          </div>
        </div>
      )}

    </div>
  );
};

// ── League row ──────────────────────────────────────────────────────
interface LeagueRowProps {
  league: League;
  isCommissioner: boolean;
  onOpen: () => void;
}

const LeagueRow: React.FC<LeagueRowProps> = ({ league, isCommissioner, onOpen }) => {
  const status = league.league_status || league.leagueStatus || 'setup';
  const { segments, isLive } = standingSummary(league);

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
        className="group flex items-center justify-between gap-4 py-4 -mx-2 px-2 cursor-pointer hover:bg-secondary-50 transition-colors"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h3 className="font-semibold text-ink-900 truncate">{league.name}</h3>
            <StatusChip status={status} />
            {isCommissioner && (
              <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-secondary-500">
                Commissioner
              </span>
            )}
            {isLive && (
              <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-wider text-gold-700">
                <span className="w-1.5 h-1.5 bg-gold" aria-hidden="true" />
                Live
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-secondary-600">{segments.join(' · ')}</p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isCommissioner && (
            <Link
              to={`/app/leagues/${league.id}/settings`}
              aria-label="League settings"
              onClick={e => e.stopPropagation()}
              className="p-1.5 text-secondary-400 hover:text-ink-900 transition-colors"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </Link>
          )}
          <ArrowRightIcon className="w-5 h-5 text-secondary-400 group-hover:text-ink-900 transition-colors" />
        </div>
      </div>
    </li>
  );
};

export default LeagueListPage;
